import React, { useRef, useCallback, ReactNode } from "react";
import { View, StyleSheet, Pressable, ScrollView, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { TableCellProps, ColumnDef } from "./types";
import useContextMenu from "@/hooks/useContextMenu";
import { useToolTip } from "@/hooks/useToolTip";

function getColumnStyle(column: ColumnDef<any>) {
  const style: any = {};

  if (column.width) {
    style.width = column.width;
  }
  if (column.minWidth) {
    style.minWidth = column.minWidth;
  }
  if (column.maxWidth) {
    style.maxWidth = column.maxWidth;
  }
  if (column.flex) {
    style.flex = column.flex;
  }
  if (!column.width && !column.flex) {
    style.flex = 1;
  }

  return style;
}

function getAlignStyle(align?: "left" | "center" | "right") {
  switch (align) {
    case "center":
      return { justifyContent: "center" as const, alignItems: "center" as const };
    case "right":
      return { justifyContent: "flex-end" as const, alignItems: "flex-end" as const };
    default:
      return { justifyContent: "flex-start" as const, alignItems: "flex-start" as const };
  }
}

// Wrapper component to add tooltip support to any cell content
interface TooltipWrapperProps {
  children: ReactNode;
  tooltipContent: ReactNode | string | null;
  style?: any;
}

function TooltipWrapper({ children, tooltipContent, style }: TooltipWrapperProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    if (!tooltipContent) return;
    isHovering.current = true;

    // If tooltip is a string, wrap it in a styled container
    const content =
      typeof tooltipContent === "string" ? (
        <View
          style={{
            backgroundColor: colors.background,
            paddingHorizontal: 10,
            paddingVertical: 6,
            borderRadius: 8,
            borderWidth: StyleSheet.hairlineWidth,
            borderColor: colors.border,
            maxWidth: 300,
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
          }}
        >
          <ThemedText style={{ fontSize: 12 }}>{tooltipContent}</ThemedText>
        </View>
      ) : (
        tooltipContent
      );

    toolTip.setToolTipContent(content);

    ref.current?.measureInWindow((x, y) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x, y: y - 36 } as any);
      toolTip.setVisible(true);
    });
  }, [tooltipContent, colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  return (
    <View ref={ref} style={style} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
      {children}
    </View>
  );
}

export function TableCell({
  value,
  column,
  row,
  index,
  isEditing,
  onValueChange,
  showTooltipWhileEditing,
}: TableCellProps) {
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const cellRef = useRef<View>(null);
  const columnStyle = getColumnStyle(column);
  const alignStyle = getAlignStyle(column.align);

  // Compute tooltip content (if defined)
  // By default, hide tooltip while editing unless showTooltipWhileEditing is true
  const shouldHideTooltip = isEditing && !showTooltipWhileEditing;
  const tooltipContent =
    !shouldHideTooltip && column.tooltip
      ? typeof column.tooltip === "function"
        ? column.tooltip(value, row, index)
        : column.tooltip
      : null;

  // If in edit mode and column is editable, render appropriate editor
  if (isEditing && column.editable && onValueChange) {
    // Text input editor
    if (column.editable.type === "text") {
      const inputWidth = column.editable.width;
      return (
        <View style={[styles.cell, columnStyle, alignStyle]}>
          <TextInput
            style={[
              styles.textInput,
              { borderColor: colors.border, backgroundColor: colors.backgroundSecondary, color: colors.text },
              inputWidth ? { width: inputWidth, flex: undefined } : undefined,
            ]}
            value={value ?? ""}
            onChangeText={onValueChange}
            placeholder={column.editable.placeholder}
            placeholderTextColor={colors.textSecondary}
            autoFocus
          />
        </View>
      );
    }

    // Picker editor (default)
    const options = column.editable.options || [];
    const currentOption = options.find((opt) => opt.value === value);
    const displayLabel =
      currentOption?.label || currentOption?.value || value || column.editable.placeholder || "Select...";

    const handleOpenPicker = () => {
      cellRef.current?.measureInWindow((x, y, w, h) => {
        contextMenu.setTriggerElement?.({ x, y, width: w, height: h });
        contextMenu.setLocation({ x, y: y + h + 4 });
        contextMenu.setMenuContent(
          <ScrollView style={styles.menuScroll}>
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <Pressable
                  key={option.value}
                  onPress={() => {
                    onValueChange(option.value);
                    contextMenu.setVisible(false);
                  }}
                  style={({ pressed }) => [
                    styles.menuItem,
                    { borderBottomColor: colors.border },
                    isSelected && { backgroundColor: colors.primary + "20" },
                    pressed && { opacity: 0.7 },
                  ]}
                >
                  {option.color && <View style={[styles.colorDot, { backgroundColor: option.color }]} />}
                  <ThemedText style={[styles.menuItemText, isSelected && { color: colors.primary }]}>
                    {option.label || option.value}
                  </ThemedText>
                  {isSelected && <MaterialCommunityIcons name="check" size={18} color={colors.primary} />}
                </Pressable>
              );
            })}
          </ScrollView>,
        );
        contextMenu.setVisible(true);
      });
    };

    return (
      <View ref={cellRef} style={[styles.cell, columnStyle, alignStyle]}>
        <Pressable
          onPress={handleOpenPicker}
          style={({ pressed }) => [
            styles.editableCell,
            { borderColor: colors.border, backgroundColor: colors.backgroundSecondary },
            pressed && { opacity: 0.7 },
          ]}
        >
          {currentOption?.color && <View style={[styles.colorDot, { backgroundColor: currentOption.color }]} />}
          <ThemedText style={styles.editableText} numberOfLines={1}>
            {displayLabel}
          </ThemedText>
          <MaterialCommunityIcons name="chevron-down" size={16} color={colors.textSecondary} />
        </Pressable>
      </View>
    );
  }

  // Check if value is valid for link styling (not null, empty, or placeholder)
  const hasValidValue = value != null && value !== "" && value !== "—";

  // Helper to conditionally wrap content in TooltipWrapper
  const wrapWithTooltip = (content: ReactNode, cellStyle: any) => {
    if (tooltipContent) {
      return (
        <TooltipWrapper tooltipContent={tooltipContent} style={cellStyle}>
          {content}
        </TooltipWrapper>
      );
    }
    return <View style={cellStyle}>{content}</View>;
  };

  // If custom render function provided, use it
  if (column.render) {
    const content = column.render(value, row, index, isEditing);

    // Wrap in Pressable if column has onPress, not editing, and has valid value
    if (column.onPress && !isEditing && hasValidValue) {
      const pressableContent = (
        <Pressable
          onPress={() => column.onPress!(row, index)}
          style={({ pressed }) => [styles.cellInner, pressed && { opacity: 0.7 }]}
        >
          {content}
        </Pressable>
      );
      return wrapWithTooltip(pressableContent, [styles.cell, columnStyle, alignStyle]);
    }

    return wrapWithTooltip(content, [styles.cell, columnStyle, alignStyle]);
  }

  // Default text rendering
  const displayValue = value == null ? "" : String(value);

  // Wrap in Pressable if column has onPress, not editing, and has valid value
  if (column.onPress && !isEditing && hasValidValue) {
    const pressableContent = (
      <Pressable
        onPress={() => column.onPress!(row, index)}
        style={({ pressed }) => [styles.cellInner, pressed && { opacity: 0.7 }]}
      >
        <ThemedText
          style={[styles.cellText, styles.linkText, column.cellStyle, { color: colors.primary }]}
          numberOfLines={1}
        >
          {displayValue}
        </ThemedText>
      </Pressable>
    );
    return wrapWithTooltip(pressableContent, [styles.cell, columnStyle, alignStyle]);
  }

  const textContent = (
    <ThemedText style={[styles.cellText, column.cellStyle]} numberOfLines={1}>
      {displayValue}
    </ThemedText>
  );
  return wrapWithTooltip(textContent, [styles.cell, columnStyle, alignStyle]);
}

const styles = StyleSheet.create({
  cell: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    justifyContent: "center",
  },
  cellInner: {
    flex: 1,
  },
  cellText: {
    fontSize: 13,
  },
  linkText: {
    textDecorationLine: "underline",
  },
  editableCell: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    gap: 6,
  },
  editableText: {
    fontSize: 12,
    flex: 1,
  },
  textInput: {
    flex: 1,
    fontSize: 12,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  menuScroll: {
    maxHeight: 300,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 8,
  },
  menuItemText: {
    fontSize: 14,
    flex: 1,
  },
});
